import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, CardTitle, Badge, Spinner, Input, Label,
  Alert, Table, Nav, NavItem, NavLink, Progress, Button
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { apiJson } from "../../../helpers/apiConfig";
import { downloadCsv } from "../../../helpers/csvExport";

/**
 * Aktivasyon Analizi — "Üye olan kullanıcı ilk ilanını ne zaman açıyor?"
 *
 * Tüm hesap SUNUCUDA (api/AdminAktivasyon). Panel yalnızca gösterir.
 * Sebebi: metrikler 8.000+ kullanıcı x 1.500+ ilan kesişimi üzerinden yüzdelik
 * ve kohort hesaplıyor; bunu OData ile satır çekip tarayıcıda toplamak hem
 * $top limitine takılır (sayılar sessizce eksik çıkar — bkz. platformStats.js
 * vakası) hem de her filtre değişiminde megabaytlarca veri indirir.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * "OLGUN TABAN" NE DEMEK — bu sayfanın en sık yanlış okunan yeri
 * ────────────────────────────────────────────────────────────────────────────
 * Dün kaydolmuş bir kullanıcı "7 gün içinde ilan açmadı" diye sayılamaz; henüz
 * 7 günü dolmadı. Bu yüzden her süre kovasının yüzdesi, YALNIZCA o süreyi
 * tamamlamış kullanıcı sayısına bölünür. Aksi halde çok üye alınan bir haftadan
 * sonra aktivasyon oranı yapay olarak çöker ve olmayan bir sorun kovalanır.
 */

const GUN = 24 * 60;

/** Dakikayı okunur süreye çevirir. Dağılım çok geniş (1 dk – 60 gün). */
const sureYaz = (dk) => {
  if (dk === null || dk === undefined || Number.isNaN(Number(dk))) return "—";
  const d = Math.round(Number(dk));
  if (d < 1) return "1 dk'dan az";
  if (d < 60) return `${d} dk`;
  if (d < GUN) {
    const saat = Math.floor(d / 60);
    const kalan = d % 60;
    return kalan ? `${saat} sa ${kalan} dk` : `${saat} sa`;
  }
  const gun = Math.floor(d / GUN);
  const saat = Math.floor((d % GUN) / 60);
  return saat ? `${gun} gün ${saat} sa` : `${gun} gün`;
};

const yuzde = (v) =>
  v === null || v === undefined ? "—" : `%${Number(v).toFixed(1).replace(".", ",")}`;

const sayi = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("tr-TR"));

const isoGun = (d) => d.toISOString().slice(0, 10);

const bugun = () => new Date();
const gunOnce = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const tarihYaz = (iso, tip) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  if (tip === "ay") return d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  const metin = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  return tip === "hafta" ? `${metin} haftası` : metin;
};

/** KPI kutusu. */
const Kpi = ({ baslik, deger, altBilgi, renk = "primary", ikon }) => (
  <Card className="mini-stats-wid h-100">
    <CardBody>
      <div className="d-flex">
        <div className="flex-grow-1">
          <p className="text-muted fw-medium mb-2">{baslik}</p>
          <h4 className="mb-0">{deger}</h4>
          {altBilgi ? <p className="text-muted mb-0 mt-2 small">{altBilgi}</p> : null}
        </div>
        <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
          <span className={`avatar-title rounded-circle bg-${renk}`}>
            <i className={`bx ${ikon} font-size-24`} />
          </span>
        </div>
      </div>
    </CardBody>
  </Card>
);

const AktivasyonAnalizi = () => {
  document.title = "Aktivasyon Analizi | HarmanaGEL";

  // ── Filtreler ──────────────────────────────────────────────────────────
  // Varsayılan 90 gün: daha geniş aralık eski (Haziran öncesi) düşük hacimli
  // dönemi karıştırıp bugünkü tabloyu bulandırıyor.
  const [bas, setBas] = useState(isoGun(gunOnce(90)));
  const [bit, setBit] = useState(isoGun(bugun()));
  const [il, setIl] = useState("");
  const [platform, setPlatform] = useState("");
  const [hesapTuru, setHesapTuru] = useState("");
  const [kaynak, setKaynak] = useState("");
  const [kohortTipi, setKohortTipi] = useState("hafta");

  // ── Veri ───────────────────────────────────────────────────────────────
  const [ozet, setOzet] = useState(null);
  const [kohort, setKohort] = useState([]);
  const [ilKirilim, setIlKirilim] = useState([]);
  const [platformKirilim, setPlatformKirilim] = useState([]);
  const [kaynakKirilim, setKaynakKirilim] = useState([]);
  const [hatirlatma, setHatirlatma] = useState(null);
  const [uretkenlik, setUretkenlik] = useState(null);

  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);

  const sorguDizesi = useMemo(() => {
    const p = new URLSearchParams();
    if (bas) p.set("bas", bas);
    if (bit) p.set("bit", bit);
    if (il) p.set("il", il);
    if (platform) p.set("platform", platform);
    if (hesapTuru) p.set("hesapTuru", hesapTuru);
    if (kaynak) p.set("kaynak", kaynak);
    return p.toString();
  }, [bas, bit, il, platform, hesapTuru, kaynak]);

  const getir = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      // Kırılımlar bilerek FİLTRESİZ (yalnızca tarih aralığı): "hangi ilde
      // aktivasyon düşük" sorusunun cevabı, il filtresi uygulanmış bir tabloda
      // aranamaz. Tarih aralığı ise ortak kalır ki KPI'larla aynı dönemi anlatsın.
      const tarihSorgu = new URLSearchParams({ bas, bit }).toString();

      const [o, k, kIl, kPlatform, kKaynak, h, u] = await Promise.all([
        apiJson(`/api/AdminAktivasyon/Ozet?${sorguDizesi}`),
        apiJson(`/api/AdminAktivasyon/Kohort?tip=${kohortTipi}&${sorguDizesi}`),
        apiJson(`/api/AdminAktivasyon/Kirilim?boyut=il&${tarihSorgu}&limit=20`),
        apiJson(`/api/AdminAktivasyon/Kirilim?boyut=platform&${tarihSorgu}`),
        apiJson(`/api/AdminAktivasyon/Kirilim?boyut=kaynak&${tarihSorgu}`),
        apiJson(`/api/AdminAktivasyon/HatirlatmaDurum`),
        // Üretkenlik kırılım filtrelerini almıyor (yalnızca tarih): "hangi
        // kategoride kaç ilan" sorusu, il/platform filtresi uygulanmış bir
        // tabloda sorulamaz.
        apiJson(`/api/AdminAktivasyon/Uretkenlik?tip=${kohortTipi}&${tarihSorgu}`),
      ]);

      setOzet(o);
      setUretkenlik(u);
      setKohort(Array.isArray(k) ? k : []);
      setIlKirilim(Array.isArray(kIl) ? kIl : []);
      setPlatformKirilim(Array.isArray(kPlatform) ? kPlatform : []);
      setKaynakKirilim(Array.isArray(kKaynak) ? kKaynak : []);
      setHatirlatma(h);
    } catch (e) {
      setHata(e.message || "Veriler alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, [sorguDizesi, kohortTipi, bas, bit]);

  useEffect(() => {
    getir();
  }, [getir]);

  /**
   * Kohort tablosunu CSV indirir. Reklam tarafı bu veriyi kendi raporlarıyla
   * (Meta Ads Manager harcaması) yan yana koymak istiyor; ekrandan elle
   * kopyalamak yerine dosya vermek hata payını sıfırlıyor.
   */
  const kohortuIndir = () => {
    if (!kohort.length) return;
    downloadCsv(
      `aktivasyon-kohort-${kohortTipi}-${bas}_${bit}.csv`,
      [
        "Kohort",
        "Yeni uye",
        "Ilk ilan acan",
        "Aktivasyon orani %",
        "Ilk 1 saat",
        "Ilk 1 saat %",
        "Ilk 24 saat",
        "Ilk 24 saat %",
        "Ilk 7 gun",
        "Ilk 7 gun %",
        "7 gunu dolan",
        "Olgun mu",
      ],
      kohort.map((k) => [
        tarihYaz(k.Kohort, kohortTipi),
        k.Uye,
        k.IlanAcan,
        k.AktivasyonOrani,
        k.Ilk1Saat,
        k.Ilk1SaatOran,
        k.Ilk24Saat,
        k.Ilk24SaatOran,
        k.Ilk7Gun,
        // null = kohort henüz 7 gününü doldurmadı, oran hesaplanamaz.
        // 0 yazmak "kimse açmadı" gibi okunurdu.
        k.Ilk7GunOran === null ? "" : k.Ilk7GunOran,
        k.Olgun7Gun,
        k.Olgun ? "Evet" : "Hayir",
      ])
    );
  };

  // ── Huni grafiği ───────────────────────────────────────────────────────
  const huniSeri = useMemo(() => {
    if (!ozet?.Kovalar) return { seri: [], etiket: [] };
    const kovalar = [...ozet.Kovalar];
    return {
      seri: [
        { name: "Kullanıcı", data: kovalar.map((k) => k.Adet) },
      ],
      etiket: kovalar.map((k) => k.Ad),
      oranlar: kovalar.map((k) => k.Oran),
    };
  }, [ozet]);

  const huniOpsiyon = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%" } },
      colors: ["#34c38f"],
      dataLabels: {
        enabled: true,
        formatter: (val) => sayi(val),
      },
      xaxis: { categories: huniSeri.etiket },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            // Oran = tüm yeni üyeler içindeki pay (hacim payı). Dönüşüm oranı
            // DEĞİL — o, olgun tabana göre hesaplanan yandaki tabloda.
            const oran = huniSeri.oranlar?.[opts?.dataPointIndex];
            return oran !== undefined
              ? `${sayi(val)} kullanıcı — yeni üyelerin ${yuzde(oran)}'i`
              : sayi(val);
          },
        },
      },
      grid: { borderColor: "#f1f1f1" },
    }),
    [huniSeri]
  );

  // ── Kohort grafiği ─────────────────────────────────────────────────────
  const kohortGrafik = useMemo(() => {
    if (!kohort.length) return null;
    return {
      seri: [
        { name: "Yeni üye", type: "column", data: kohort.map((k) => k.Uye) },
        { name: "İlk ilan açan", type: "column", data: kohort.map((k) => k.IlanAcan) },
        {
          name: "Aktivasyon oranı (%)",
          type: "line",
          data: kohort.map((k) => Number(k.AktivasyonOrani)),
        },
      ],
      opsiyon: {
        chart: { type: "line", stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 0, 3], curve: "smooth" },
        colors: ["#556ee6", "#34c38f", "#f1b44c"],
        // İki eksen şart: üye sayısı binlerle, oran 0-100 arası. Tek eksende
        // oran çizgisi taban çizgisine yapışır ve trend görünmez.
        yaxis: [
          { title: { text: "Kullanıcı" } },
          { show: false },
          { opposite: true, title: { text: "Oran (%)" }, min: 0 },
        ],
        xaxis: { categories: kohort.map((k) => tarihYaz(k.Kohort, kohortTipi)) },
        legend: { position: "top" },
        grid: { borderColor: "#f1f1f1" },
      },
    };
  }, [kohort, kohortTipi]);

  const hatirlatmaAcik =
    String(hatirlatma?.Ayarlar?.ILAN_HATIRLATMA_AKTIF || "false").trim().toLowerCase() === "true";

  // ── Kırılım tablosu ────────────────────────────────────────────────────
  const KirilimTablosu = ({ baslik, satirlar, aciklama }) => (
    <Card className="h-100">
      <CardBody>
        <CardTitle className="mb-1">{baslik}</CardTitle>
        {aciklama ? <p className="text-muted small mb-3">{aciklama}</p> : null}
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Ad</th>
                <th className="text-end">Üye</th>
                <th className="text-end">İlan açan</th>
                <th className="text-end">Aktivasyon</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">Kayıt yok</td>
                </tr>
              ) : (
                satirlar.map((r) => (
                  <tr key={r.Ad}>
                    <td>{r.Ad}</td>
                    <td className="text-end">{sayi(r.Uye)}</td>
                    <td className="text-end">{sayi(r.IlanAcan)}</td>
                    <td className="text-end" style={{ minWidth: 130 }}>
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <span>{yuzde(r.AktivasyonOrani)}</span>
                        <Progress
                          value={Math.min(100, Number(r.AktivasyonOrani) * 3)}
                          color={Number(r.AktivasyonOrani) >= 15 ? "success" : "warning"}
                          style={{ width: 60, height: 6 }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        {/* İlerleme çubuğu 3x ölçeklendi: aktivasyon oranları %5-20 bandında
            seyrediyor, 1:1 ölçekte tüm çubuklar boş görünüyordu. */}
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Aktivasyon Analizi" />

          {hata ? <Alert color="danger">{hata}</Alert> : null}

          {/* ── FİLTRELER ────────────────────────────────────────────── */}
          <Card>
            <CardBody>
              <Row className="g-3 align-items-end">
                <Col md={2}>
                  <Label className="form-label">Başlangıç</Label>
                  <Input type="date" value={bas} onChange={(e) => setBas(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label className="form-label">Bitiş</Label>
                  <Input type="date" value={bit} onChange={(e) => setBit(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label className="form-label">Platform</Label>
                  <Input type="select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                    <option value="">Tümü</option>
                    <option value="ios">iOS</option>
                    <option value="android">Android</option>
                    <option value="web">Web</option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label">Hesap türü</Label>
                  <Input type="select" value={hesapTuru} onChange={(e) => setHesapTuru(e.target.value)}>
                    <option value="">Tümü</option>
                    <option value="Kullanici">Kullanıcı</option>
                    <option value="Satıcı">Satıcı</option>
                    <option value="Üretici">Üretici</option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label">İl</Label>
                  <Input
                    placeholder="Örn. Konya"
                    value={il}
                    onChange={(e) => setIl(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="form-label">Trafik kaynağı</Label>
                  {/* Seçenekler kırılım tablosundan besleniyor: kaynak değerleri
                      Play Install Referrer'dan serbest metin olarak geliyor
                      ("facebook / cpc", "google-play / organic"), sabit bir
                      liste yazmak yeni kampanyalarda eksik kalırdı. */}
                  <Input type="select" value={kaynak} onChange={(e) => setKaynak(e.target.value)}>
                    <option value="">Tümü</option>
                    {kaynakKirilim.map((r) => (
                      <option key={r.Ad} value={r.Ad}>
                        {r.Ad} ({sayi(r.Uye)})
                      </option>
                    ))}
                  </Input>
                </Col>
                {/* Altı filtre tam 12 kolon tutuyor; butonlar kendi satırında,
                    sağa yaslı. Aynı satıra sıkıştırmak filtreleri daraltıp
                    "Afyonkarahisar" gibi uzun il adlarını kırpıyordu. */}
                <Col md={12} className="d-flex justify-content-end gap-2">
                  <Button
                    color="light"
                    onClick={kohortuIndir}
                    disabled={!kohort.length}
                    title="Kohort tablosunu CSV olarak indir"
                  >
                    <i className="bx bx-download me-1" />
                    CSV
                  </Button>
                  <Button color="primary" onClick={getir} disabled={yukleniyor}>
                    {yukleniyor ? <Spinner size="sm" /> : "Uygula"}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {yukleniyor && !ozet ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : null}

          {ozet ? (
            <>
              {/* ── KPI ──────────────────────────────────────────────── */}
              <Row>
                <Col md={6} xl={3} className="mb-3">
                  <Kpi
                    baslik="Yeni üye"
                    deger={sayi(ozet.ToplamUye)}
                    altBilgi="Seçili dönemde kaydolan"
                    renk="primary"
                    ikon="bx-user-plus"
                  />
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <Kpi
                    baslik="İlk ilanını açan"
                    deger={sayi(ozet.IlanAcan)}
                    altBilgi={`Aktivasyon oranı: ${yuzde(ozet.AktivasyonOrani)}`}
                    renk="success"
                    ikon="bx-check-circle"
                  />
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <Kpi
                    baslik="Ortalama ilk ilan süresi"
                    deger={sureYaz(ozet.Sure?.MedyanDk)}
                    // Ortalama tek başına yanıltıcı: dağılım aşırı sağa çarpık,
                    // birkaç "60 gün sonra açan" kullanıcı ortalamayı günlere
                    // taşıyor. Medyan büyük gösterilip ortalama altta veriliyor.
                    altBilgi={`Medyan · ortalama ${sureYaz(ozet.Sure?.OrtalamaDk)} · %90'ı ${sureYaz(
                      ozet.Sure?.P90Dk
                    )} içinde`}
                    renk="info"
                    ikon="bx-time-five"
                  />
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <Kpi
                    baslik="Hiç ilan açmayan"
                    deger={sayi(ozet.HicAcmayan)}
                    altBilgi={`7 günü dolup açmayan: ${sayi(ozet.Olgun7GunAcmayan)} (${yuzde(
                      ozet.Olgun7GunAcmayanOran
                    )})`}
                    renk="danger"
                    ikon="bx-user-x"
                  />
                </Col>
              </Row>

              {/* ── HUNİ ─────────────────────────────────────────────── */}
              <Row>
                <Col xl={7} className="mb-3">
                  <Card className="h-100">
                    <CardBody>
                      <CardTitle className="mb-1">İlk ilana kadar geçen süre</CardTitle>
                      <p className="text-muted small mb-3">
                        Yüzdeler, o süreyi tamamlamış (olgun) kullanıcı tabanına göredir.
                      </p>
                      <ReactApexChart
                        options={huniOpsiyon}
                        series={huniSeri.seri}
                        type="bar"
                        height={300}
                      />
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5} className="mb-3">
                  <Card className="h-100">
                    <CardBody>
                      <CardTitle className="mb-3">Kümülatif dönüşüm</CardTitle>
                      <div className="table-responsive">
                        <Table className="table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Pencere</th>
                              <th className="text-end">Bu aralıkta</th>
                              <th className="text-end">Bu süreye kadar</th>
                              <th className="text-end">Olgun taban</th>
                              <th className="text-end">Dönüşüm</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ozet.Kovalar.map((k) => (
                              <tr key={k.Ad}>
                                <td>{k.Ad}</td>
                                <td className="text-end">{sayi(k.Adet)}</td>
                                <td className="text-end">{sayi(k.Kumulatif)}</td>
                                <td className="text-end text-muted">{sayi(k.OlgunTaban)}</td>
                                <td className="text-end">
                                  <Badge color="light" className="text-body">
                                    {yuzde(k.KumulatifOran)}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="small text-muted mb-1">Süre dağılımı (ilan açanlar)</div>
                        <div className="d-flex flex-wrap gap-3">
                          <div><strong>%25</strong>: {sureYaz(ozet.Sure?.P25Dk)}</div>
                          <div><strong>Medyan</strong>: {sureYaz(ozet.Sure?.MedyanDk)}</div>
                          <div><strong>%75</strong>: {sureYaz(ozet.Sure?.P75Dk)}</div>
                          <div><strong>%90</strong>: {sureYaz(ozet.Sure?.P90Dk)}</div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* ── KOHORT ───────────────────────────────────────────── */}
              <Card className="mb-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <CardTitle className="mb-0">Kohort analizi</CardTitle>
                    <Nav pills>
                      {[
                        ["gun", "Günlük"],
                        ["hafta", "Haftalık"],
                        ["ay", "Aylık"],
                      ].map(([deger, etiket]) => (
                        <NavItem key={deger}>
                          <NavLink
                            href="#"
                            active={kohortTipi === deger}
                            onClick={(e) => {
                              e.preventDefault();
                              setKohortTipi(deger);
                            }}
                          >
                            {etiket}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </div>

                  {kohortGrafik ? (
                    <ReactApexChart
                      options={kohortGrafik.opsiyon}
                      series={kohortGrafik.seri}
                      type="line"
                      height={320}
                    />
                  ) : (
                    <p className="text-muted text-center py-4 mb-0">Bu aralıkta kayıt yok.</p>
                  )}

                  <div className="table-responsive mt-3">
                    <Table className="table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Kohort</th>
                          <th className="text-end">Yeni üye</th>
                          <th className="text-end">İlk 1 saat</th>
                          <th className="text-end">İlk 24 saat</th>
                          <th className="text-end">İlk 7 gün</th>
                          <th className="text-end">Toplam aktivasyon</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kohort.map((k) => (
                          <tr key={k.Kohort}>
                            <td>
                              {tarihYaz(k.Kohort, kohortTipi)}{" "}
                              {/* Olgunlaşmamış kohortun 7 günlük oranı henüz
                                  nihai değil; işaretlenmezse "son hafta çöktü"
                                  diye yanlış okunur. */}
                              {!k.Olgun ? (
                                <Badge color="warning" pill className="ms-1">
                                  devam ediyor
                                </Badge>
                              ) : null}
                            </td>
                            <td className="text-end">{sayi(k.Uye)}</td>
                            <td className="text-end">
                              {sayi(k.Ilk1Saat)}{" "}
                              <span className="text-muted small">({yuzde(k.Ilk1SaatOran)})</span>
                            </td>
                            <td className="text-end">
                              {sayi(k.Ilk24Saat)}{" "}
                              <span className="text-muted small">({yuzde(k.Ilk24SaatOran)})</span>
                            </td>
                            <td className="text-end">
                              {sayi(k.Ilk7Gun)}{" "}
                              <span className="text-muted small">
                                ({k.Ilk7GunOran === null ? "—" : yuzde(k.Ilk7GunOran)})
                              </span>
                            </td>
                            <td className="text-end">
                              <Badge color={Number(k.AktivasyonOrani) >= 12 ? "success" : "warning"}>
                                {yuzde(k.AktivasyonOrani)}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              {/* ── KIRILIMLAR ───────────────────────────────────────── */}
              <Row>
                <Col xl={5} className="mb-3">
                  <KirilimTablosu
                    baslik="Şehre göre"
                    satirlar={ilKirilim}
                    aciklama="En çok üye alan 20 il. Üye sayısı yüksek ama aktivasyonu düşük iller, reklam bütçesinin boşa gittiği yerlerdir."
                  />
                </Col>
                <Col xl={3} className="mb-3">
                  <KirilimTablosu
                    baslik="Platforma göre"
                    satirlar={platformKirilim}
                    aciklama="Kayıt anındaki platform. Eski kayıtlarda FCM token'ından çıkarıldı."
                  />
                </Col>
                <Col xl={4} className="mb-3">
                  <KirilimTablosu
                    baslik="Trafik kaynağına göre"
                    satirlar={kaynakKirilim}
                    aciklama="Kayıt kaynağı yeni toplanmaya başladı; geçmiş kayıtlar 'bilinmiyor' görünür."
                  />
                </Col>
              </Row>

              {/* ── ÜRETKENLİK ───────────────────────────────────────── */}
              {uretkenlik ? (
                <>
                  <Card className="mb-3">
                    <CardBody>
                      <CardTitle className="mb-1">
                        İlan üretkenliği
                        <small className="text-muted ms-2 fw-normal">
                          ({tarihYaz(bas, "gun")} – {tarihYaz(bit, "gun")})
                        </small>
                      </CardTitle>
                      <p className="text-muted small mb-3">
                        Aktivasyon <strong>kaç kişinin</strong> ilan açtığını ölçer; bu bölüm{" "}
                        <strong>kaç ilan</strong> açıldığını. 100 kişinin 1'er ilan açması ile
                        10 kişinin 10'ar ilan açması aynı ilan sayısını verir ama bambaşka
                        işlerdir.
                      </p>

                      <Row className="g-3 mb-3">
                        <Col md={3}>
                          <div className="p-3 border rounded h-100">
                            <div className="text-muted small">Yeni üye</div>
                            <h5 className="mb-0 mt-1">{sayi(uretkenlik.Ozet?.ToplamYeniUye)}</h5>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 border rounded h-100">
                            <div className="text-muted small">Oluşturulan ilan</div>
                            <h5 className="mb-0 mt-1">{sayi(uretkenlik.Ozet?.ToplamIlan)}</h5>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 border rounded h-100">
                            <div className="text-muted small">İlan veren kullanıcı</div>
                            <h5 className="mb-0 mt-1">{sayi(uretkenlik.Ozet?.ToplamIlanVeren)}</h5>
                            <div className="text-muted small mt-1">En az 1 ilan açan, tekil</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 border rounded h-100">
                            <div className="text-muted small">Kullanıcı başına ilan</div>
                            <h5 className="mb-0 mt-1">
                              {String(uretkenlik.Ozet?.OrtIlanPerVeren ?? "—").replace(".", ",")}
                            </h5>
                            <div className="text-muted small mt-1">
                              İlan verenler arasında
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table className="table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Dönem</th>
                              <th className="text-end">Yeni üye</th>
                              <th className="text-end">İlan</th>
                              <th className="text-end">İlan veren</th>
                              <th className="text-end">İlan / veren</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(uretkenlik.Seri || []).map((s) => (
                              <tr key={s.Donem}>
                                <td>{tarihYaz(s.Donem, kohortTipi)}</td>
                                <td className="text-end">{sayi(s.YeniUye)}</td>
                                <td className="text-end">{sayi(s.Ilan)}</td>
                                <td className="text-end">{sayi(s.IlanVeren)}</td>
                                <td className="text-end">
                                  {String(s.IlanPerVeren).replace(".", ",")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>

                  <Row>
                    <Col xl={5} className="mb-3">
                      <Card className="h-100">
                        <CardBody>
                          <CardTitle className="mb-1">İlan veren kullanıcı dağılımı</CardTitle>
                          <p className="text-muted small mb-3">
                            Dönem içinde kaç ilan açtıklarına göre.
                          </p>
                          <div className="table-responsive">
                            <Table className="table-sm align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Grup</th>
                                  <th className="text-end">Kullanıcı</th>
                                  <th className="text-end">Payı</th>
                                  <th className="text-end">İlan</th>
                                  <th className="text-end">İlan payı</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(uretkenlik.Kovalar || []).map((k) => (
                                  <tr key={k.Ad}>
                                    <td>{k.Ad}</td>
                                    <td className="text-end">{sayi(k.Kullanici)}</td>
                                    <td className="text-end text-muted">
                                      {yuzde(k.KullaniciOran)}
                                    </td>
                                    <td className="text-end">{sayi(k.ToplamIlan)}</td>
                                    <td className="text-end">
                                      <Badge color="light" className="text-body">
                                        {yuzde(k.IlanOran)}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col xl={7} className="mb-3">
                      <Card className="h-100">
                        <CardBody>
                          <CardTitle className="mb-1">Ana kategoriye göre ilanlar</CardTitle>
                          <p className="text-muted small mb-3">
                            İlan leaf kategoride durur; buradaki değerler kategori ağacında
                            en üste kadar çıkılarak hesaplanır.
                          </p>
                          <div className="table-responsive">
                            <Table className="table-sm align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Ana kategori</th>
                                  {(uretkenlik.Seri || []).map((s) => (
                                    <th key={s.Donem} className="text-end">
                                      {tarihYaz(s.Donem, kohortTipi)}
                                    </th>
                                  ))}
                                  <th className="text-end">Toplam</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(uretkenlik.Kategoriler || []).map((k) => {
                                  // Dönem sütunları seriden geliyor; kategoride o
                                  // dönem hiç ilan yoksa satır dönmüyor → 0 yaz.
                                  const harita = {};
                                  (k.Donemler || []).forEach((d) => {
                                    harita[d.Donem] = d.Adet;
                                  });
                                  return (
                                    <tr key={k.AnaKategori}>
                                      <td>{k.AnaKategori}</td>
                                      {(uretkenlik.Seri || []).map((s) => (
                                        <td key={s.Donem} className="text-end">
                                          {sayi(harita[s.Donem] || 0)}
                                        </td>
                                      ))}
                                      <td className="text-end fw-bold">{sayi(k.Toplam)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </>
              ) : null}

              {/* ── HATIRLATMA BİLDİRİMİ ─────────────────────────────── */}
              {hatirlatma ? (
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <CardTitle className="mb-0">
                        Otomatik "ilan aç" hatırlatması
                      </CardTitle>
                      <Badge color={hatirlatmaAcik ? "success" : "secondary"} className="font-size-12">
                        {hatirlatmaAcik ? "AÇIK" : "KAPALI"}
                      </Badge>
                    </div>

                    {!hatirlatmaAcik ? (
                      <Alert color="warning" className="mb-3">
                        Hatırlatma job'u kayıtlı ama <strong>kapalı</strong>. Açmak için Sistem
                        Ayarları sayfasından <code>ILAN_HATIRLATMA_AKTIF</code> değerini{" "}
                        <code>true</code> yapın. Açıldığında{" "}
                        <strong>{sayi(hatirlatma.Ulasilabilir)}</strong> kullanıcıya kademeli olarak
                        bildirim gitmeye başlar.
                      </Alert>
                    ) : null}

                    <Row className="g-3">
                      <Col md={3}>
                        <div className="p-3 border rounded h-100">
                          <div className="text-muted small">Hiç ilan açmamış</div>
                          <h5 className="mb-0 mt-1">{sayi(hatirlatma.HicAcmayan)}</h5>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded h-100">
                          <div className="text-muted small">Bildirimle ulaşılabilir</div>
                          <h5 className="mb-0 mt-1">{sayi(hatirlatma.Ulasilabilir)}</h5>
                          <div className="text-muted small mt-1">
                            Aktif FCM token'ı olan ve bildirimi kapatmamış
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded h-100">
                          <div className="text-muted small">Gönderildi</div>
                          <h5 className="mb-0 mt-1">{sayi(hatirlatma.GonderilenKullanici)}</h5>
                          <div className="text-muted small mt-1">
                            Toplam {sayi(hatirlatma.ToplamGonderim)} bildirim
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded h-100">
                          <div className="text-muted small">Sonrasında ilan açan</div>
                          <h5 className="mb-0 mt-1">
                            {sayi(hatirlatma.SonrasindaIlanAcan)}{" "}
                            <small className="text-success">
                              {yuzde(hatirlatma.DonusumOrani)}
                            </small>
                          </h5>
                          <div className="text-muted small mt-1">
                            Bildirimin işe yarayıp yaramadığının tek ölçüsü
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive mt-3">
                      <Table className="table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Ayar</th>
                            <th>Değer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(hatirlatma.Ayarlar || {}).map(([k, v]) => (
                            <tr key={k}>
                              <td><code>{k}</code></td>
                              <td>{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AktivasyonAnalizi;
